import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { IListView } from '@shared/interfaces/various/IListView';
import { IResponsible, IResponsibleUser } from '@shared/interfaces';
import { currentReportingPeriod } from '@shared/helpers/helpers';
import { OrganisationService } from '@shared/services/organisation.service';
import { SnackService } from '@shared/services/snack.service';
import { TranslateService } from '@ngx-translate/core';
import { GuardService } from '@shared/services/guard.service';

@Component({
    selector: 'app-org-tree-node',
    templateUrl: './org-tree-node.component.html',
    styleUrls: ['./org-tree-node.component.scss'],
    standalone: false
})
export class OrgTreeNodeComponent {
    @Input() currentNode: IListView<IResponsible> & Partial<IResponsible>;
    @Output() clickNodeEvent = new EventEmitter<IResponsible>();
    @Output() deleteNode = new EventEmitter<IResponsible>();
    @Output() openNode = new EventEmitter<IResponsible>();
    @Output() editNode = new EventEmitter<IResponsible>();
    @Output() unPublish = new EventEmitter<IResponsible>();
    @Output() reset = new EventEmitter<IResponsible>();
    @Input() isParent: boolean;
    @Input() isEditActive: boolean;
    @Input() isActive: boolean;
    @Input() isDisabled = false;

    private readonly organisationService = inject(OrganisationService);
    private readonly snack = inject(SnackService);
    private readonly translate = inject(TranslateService);
    protected readonly guardService = inject(GuardService);

    get mainSpecialists(): IResponsibleUser[] {
        return (
            this.currentNode?.specialists
                ?.filter(e => e.fullAccess)
                ?.sort((a, b) => a.fullName.localeCompare(b.fullName)) ?? []
        );
    }

    get mainResponsible(): IResponsibleUser[] {
        return (
            this.currentNode?.responsible
                ?.filter(e => e.fullAccess)
                ?.sort((a, b) => a.fullName.localeCompare(b.fullName)) ?? []
        );
    }

    get responsibleFullName(): string {
        return this.mainResponsible?.map(e => e.fullName)?.join(', ');
    }

    get specialistFullName(): string {
        return this.mainSpecialists?.map(e => e.fullName)?.join(', ');
    }

    changeNode(value: IListView<IResponsible> & Partial<IResponsible>) {
        this.clickNodeEvent.emit(value);
    }

    get hasCurrentReport(): boolean {
        return this.currentNode?.lastActiveReportingPeriod === currentReportingPeriod;
    }

    protected async toggleDeactivationState(): Promise<void> {
        const newDeactivationState = !this.currentNode.deactivated;
        try {
            await this.organisationService.updateDeactivationState(this.currentNode.id, newDeactivationState);
            this.currentNode.deactivated = newDeactivationState;
        } catch (error) {
            console.error('Error updating deactivation state:', error);
            this.snack.openSnackBarError(
                this.translate.instant('organisation.snacks.error.updateDeactivationStateFailed')
            );
        }
    }
}
